import { hotelDistanceKm } from '../shared/haversine.js';
import type { ChatUiHotelAmenity } from '../shared/hotel_amenity_schema.js';
import {
  HOTEL_DIRECTIVE_RATING_FLOORS,
  type ChatUiHotelOffer,
  type HotelDirectiveRatingFloor,
  type HotelPanelFilters,
  type HotelPanelSort,
  type HotelPlannerDirectives,
  type HotelPlannerPlace,
} from './hotel_planner_schema.js';

/** Diária da oferta quando as noites são conhecidas; o total cru quando não —
 *  a mesma conta que o card faz (`perNightOf`), sem depender do módulo de
 *  quartos. */
export function nightlyAmount(
  offer: Pick<ChatUiHotelOffer, 'price'>,
  nights: number | undefined,
): number | undefined {
  const amount = offer.price?.amount;
  if (amount === undefined) return undefined;
  return nights !== undefined && nights > 0 ? amount / nights : amount;
}

/** Café/reembolso: o FILTRO lê o AGREGADO do HOTEL (`hasBreakfastRate`/
 *  `hasRefundableRate`) — o MESMO fato que o painel lê
 *  (`hotel_results_filter_view.ts` → `hasBreakfast`/`hasRefundable`). A marca
 *  do quarto mais barato (`breakfast`/`refundable`) NÃO entra: ela era a
 *  divergência card × painel (dev 18/09: café 3 × 0). Lenient no desconhecido,
 *  como o painel (`matchesRateFlag`): só `false` explícito exclui. Política é o
 *  único booleano estrito: "dentro da política" exige a marca afirmando que sim. */
function matchesStayFilters(offer: ChatUiHotelOffer, filter: HotelPanelFilters): boolean {
  if (filter.breakfastOnly && offer.hasBreakfastRate === false) return false;
  if (filter.refundableOnly && offer.hasRefundableRate === false) return false;
  if (filter.policyOnly && !isWithinPolicy(offer, filter)) return false;
  return true;
}

/** #5820: com café e/ou reembolso ligados, "dentro da política" é da TARIFA
 *  com esses atributos — o `policyStatus` é do quarto mais barato, e o acordo
 *  sem café dentro do teto aprovava hotel cujas tarifas com café estavam
 *  todas fora. Sem o fato da tarifa (`undefined`), vale o `policyStatus`, como
 *  antes. Espelho de `matchesPolicy` do painel (intelligence-ui-kit `hotel_policy_cut.ts`). */
function isWithinPolicy(offer: ChatUiHotelOffer, filter: HotelPanelFilters): boolean {
  return rateWithinPolicy(offer, filter) ?? offer.policyStatus === 'within_policy';
}

type RatePolicyFacts = Pick<
  ChatUiHotelOffer,
  | 'breakfastRateWithinPolicy'
  | 'refundableRateWithinPolicy'
  | 'breakfastRefundableRateWithinPolicy'
  | 'hasBreakfastRefundableRate'
>;

function rateWithinPolicy(offer: RatePolicyFacts, filter: HotelPanelFilters): boolean | undefined {
  // #5849: sem UMA tarifa com café E reembolso, nenhuma está dentro — café e
  // reembolso em tarifas separadas não aprovam o hotel pelo fallback.
  if (filter.breakfastOnly && filter.refundableOnly)
    return offer.hasBreakfastRefundableRate === false
      ? false
      : offer.breakfastRefundableRateWithinPolicy;
  if (filter.breakfastOnly) return offer.breakfastRateWithinPolicy;
  if (filter.refundableOnly) return offer.refundableRateWithinPolicy;
  return undefined;
}

/** Telemetria do #5820: quantas ofertas o corte conjunto leria pelo
 *  `policyStatus` do quarto mais barato por falta do fato da tarifa (o
 *  fallback de `isWithinPolicy`). `undefined` quando o corte conjunto não está
 *  ligado (política sem café nem reembolso). O painel filtra no cliente e não
 *  loga; o agente grava este número no desfecho do filtro. */
export function policyRateFallbackCount(
  offers: readonly RatePolicyFacts[],
  filter: HotelPanelFilters,
): number | undefined {
  if (!filter.policyOnly || !(filter.breakfastOnly || filter.refundableOnly)) return undefined;
  return offers.filter((offer) => rateWithinPolicy(offer, filter) === undefined).length;
}

/** Teto/piso de DIÁRIA — dado ausente reprova (ver doc do módulo). */
function matchesPriceFilters(
  offer: ChatUiHotelOffer,
  filter: HotelPanelFilters,
  nights: number | undefined,
): boolean {
  const nightly = nightlyAmount(offer, nights);
  if (
    filter.maxNightlyPrice !== undefined &&
    (nightly === undefined || nightly > filter.maxNightlyPrice)
  )
    return false;
  if (
    filter.minNightlyPrice !== undefined &&
    (nightly === undefined || nightly < filter.minNightlyPrice)
  )
    return false;
  return true;
}

/** Os pisos das faixas de nota do PAINEL, do topo pro chão — a fonte é
 *  `HOTEL_DIRECTIVE_RATING_FLOORS` (`hotel_planner_schema.ts`), o mesmo
 *  vocabulário que o agents fecha na ferramenta do modelo. */
const PANEL_RATING_BAND_FLOORS = HOTEL_DIRECTIVE_RATING_FLOORS;

/** O piso de nota EFETIVO de uma diretiva: o MENOR piso pedido, ENCOSTADO na
 *  faixa do painel mais próxima (`PANEL_RATING_BAND_FLOORS` — 3,5 / 4 / 4,5).
 *
 *  Cena = card exige um corte só: a doca lê o piso como mínimo exato, e o
 *  painel só sabe marcar FAIXAS. Sem encostar, `ratingFloors: [5]` cortava a
 *  doca em ≥5 e deixava o painel NEUTRO (nenhuma faixa ≥5 para marcar), e
 *  `[4,2]` cortava a doca em 4,2 e o painel em ≥4,5 — duas listas a partir da
 *  mesma diretiva. O wire segue aceitando 0–10: ele também carrega os pisos
 *  que o PAINEL relatou, e é aqui — na camada pura que o card e a projeção de
 *  cena do agente compartilham — que a régua se unifica. O AGENTE encosta o
 *  piso pedido pelo modelo por esta mesma função ao mesclar a diretiva
 *  (`mergeFilter`, #3393), e a fala do filtro nasce do resultado encostado.
 *
 *  Empate (ex.: 4,25) fica no piso MAIS BAIXO: alargar a lista é o erro
 *  recuperável (o cliente vê a opção a mais), estreitar esconde hotel. */
export function directiveRatingFloor(
  floors: readonly number[] | undefined,
): HotelDirectiveRatingFloor | undefined {
  if (!floors?.length) return undefined;
  const asked = Math.min(...floors);
  // `<=` com a lista do topo pro chão: no EMPATE (4,25) vence o piso mais
  // baixo, o último visitado — alargar a lista é o erro recuperável.
  return PANEL_RATING_BAND_FLOORS.reduce((best, floor) =>
    Math.abs(floor - asked) <= Math.abs(best - asked) ? floor : best,
  );
}

/** As faixas do painel que o piso efetivo acende — da encostada para cima, o
 *  que reproduz ">= piso" na união meio-aberta do `intelligence-ui-kit`.
 *  Vizinha de `directiveRatingFloor` para as duas réguas (doca e painel)
 *  nascerem da mesma linha. */
export function directiveRatingBands(
  floors: readonly number[] | undefined,
): HotelDirectiveRatingFloor[] | undefined {
  const floor = directiveRatingFloor(floors);
  return floor === undefined
    ? undefined
    : PANEL_RATING_BAND_FLOORS.filter((candidate) => candidate >= floor);
}

/** Piso de nota (o MENOR pedido, em `or`) e teto de distância — os dois
 *  reprovam dado ausente. A distância é a RÉGUA canônica (`hotelDistanceKm`,
 *  `hotel/shared/haversine.ts`): supplier `distanceKm` primeiro, haversine a
 *  partir de `place` senão — a MESMA que o painel usa
 *  (`hotel_results_filter_view.ts`) e que o card imprime ("1,2 km a pé"); ler
 *  `offer.distanceKm` cru aqui esvaziaria o dock numa busca ancorada por mapa
 *  em que o fornecedor não devolve distância. */
function matchesRatingAndDistance(
  offer: ChatUiHotelOffer,
  filter: HotelPanelFilters,
  place: HotelPlannerPlace | undefined,
): boolean {
  const floor = directiveRatingFloor(filter.ratingFloors);
  if (floor !== undefined && (offer.rating === undefined || offer.rating < floor)) return false;
  if (filter.maxDistanceKm !== undefined) {
    const distance = hotelDistanceKm(offer, place);
    if (distance === undefined || distance > filter.maxDistanceKm) return false;
  }
  return true;
}

/** A IDENTIDADE de uma comodidade — a mesma do painel (`hotelAmenityViews`,
 *  `hotel_planner_view.ts`: `id: amenity.code ?? amenity.label`) e a que o
 *  agente grava em `directives.filter.amenityIds`. */
export function hotelAmenityId(amenity: ChatUiHotelAmenity): string {
  return amenity.code ?? amenity.label;
}

/** Comodidades pedidas casam pelo id (`hotelAmenityId`) — requisitos, em `and`
 *  (diferente das faixas de nota, que se combinam em `or`). Mesma régua de
 *  `matchesAmenities` do painel (`intelligence-ui-kit/hotel_results_filters.tsx`). */
function matchesAmenities(offer: ChatUiHotelOffer, filter: HotelPanelFilters): boolean {
  if (!filter.amenityIds?.length) return true;
  const have = new Set((offer.amenities ?? []).map(hotelAmenityId));
  return filter.amenityIds.every((id) => have.has(id));
}

/** Um filtro de diretiva casa a oferta? Dado AUSENTE reprova filtros que
 *  precisam dele (preço, distância, nota): "só até 2 km" não pode incluir um
 *  hotel sem distância — é a mesma ausência honesta do retrato. Cada eixo é
 *  uma função própria (teto de complexidade do lint); aqui só se combina. */
export function matchesDirectiveFilter(
  offer: ChatUiHotelOffer,
  filter: HotelPanelFilters,
  nights?: number,
  place?: HotelPlannerPlace,
): boolean {
  return (
    matchesStayFilters(offer, filter) &&
    matchesPriceFilters(offer, filter, nights) &&
    matchesRatingAndDistance(offer, filter, place) &&
    matchesAmenities(offer, filter)
  );
}

/** `stars` é a NOTA (0–5, `rating`), NÃO a classificação do prédio
 *  (`category`) — o mesmo eixo que o painel ordena
 *  (`intelligence-ui-kit/hotel_results_sort.tsx`, caso `'stars'` → `a.rating`).
 *  `distance` usa a régua canônica (`hotelDistanceKm`), por isso os
 *  comparadores recebem `place` — os outros eixos o ignoram. */
const SORTERS: Record<
  HotelPanelSort,
  (a: ChatUiHotelOffer, b: ChatUiHotelOffer, place: HotelPlannerPlace | undefined) => number
> = {
  recommended: () => 0,
  price: (a, b) => (a.price?.amount ?? Infinity) - (b.price?.amount ?? Infinity),
  distance: (a, b, place) =>
    (hotelDistanceKm(a, place) ?? Infinity) - (hotelDistanceKm(b, place) ?? Infinity),
  reviews: (a, b) => (b.reviewCount ?? -1) - (a.reviewCount ?? -1),
  stars: (a, b) => (b.rating ?? -1) - (a.rating ?? -1),
};

/** A lista que o card MOSTRA e a que o agente PROJETA como cena, a partir do
 *  mesmo bloco: filtro primeiro, ordem depois, ordem estável (`Array.sort` é
 *  estável no V8). Sem diretivas devolve uma cópia na ordem do bloco. `place`
 *  é a âncora da busca (`ui.placeDetails`) — a mesma que o painel e o card
 *  passam para `hotelDistanceKm`, necessária para `maxDistanceKm`/`sortBy:
 *  'distance'` quando o fornecedor não devolve `distanceKm`. */
export function applyHotelPlannerDirectives<T extends ChatUiHotelOffer>(
  offers: readonly T[],
  directives: HotelPlannerDirectives | undefined,
  nights?: number,
  place?: HotelPlannerPlace,
): T[] {
  if (!directives) return [...offers];
  const filter = directives.filter;
  const filtered = filter
    ? offers.filter((offer) => matchesDirectiveFilter(offer, filter, nights, place))
    : [...offers];
  const sortBy = directives.sortBy;
  return sortBy ? [...filtered].sort((a, b) => SORTERS[sortBy](a, b, place)) : filtered;
}
